import { CATEGORIES, CATEGORY_LAUNCH_MIN, getAllCategories } from './categories';
import { BASELINE_AS_OF, todayAsOf } from './bank-dates';
import { QUESTION_CATALOG } from './catalog.generated';
export function buildCatalog(questions) {
    const catalog = {};
    for (const q of questions) {
        // Retired questions (shared/src/quarantine.ts) are on their way out of every pool.
        if (q.type !== 'multiple-choice' || q.retiredOn)
            continue;
        const entry = (catalog[q.category] ??= { count: 0, themes: {} });
        entry.count += 1;
        if (q.theme)
            entry.themes[q.theme] = (entry.themes[q.theme] ?? 0) + 1;
    }
    // Stable key order so the generated file only changes when counts do.
    const sorted = {};
    for (const key of Object.keys(catalog).sort()) {
        const themes = {};
        for (const t of Object.keys(catalog[key].themes).sort())
            themes[t] = catalog[key].themes[t];
        sorted[key] = { count: catalog[key].count, themes };
        const liveFrom = CATEGORIES[key]?.gated ? launchDate(questions.filter(q => q.category === key && q.type === 'multiple-choice')) : undefined;
        if (liveFrom)
            sorted[key].liveFrom = liveFrom;
    }
    return sorted;
}
/**
 * The day a gated topic launches: when its CATEGORY_LAUNCH_MIN-th question (by date) goes live.
 * Retired questions still count, so a later retirement can't make a launched topic vanish.
 * Derived from the questions' own dates, so re-dating a batch (npm run bank -- redate) moves the launch with it.
 */
export function launchDate(questions) {
    if (questions.length < CATEGORY_LAUNCH_MIN)
        return undefined;
    const days = questions.map(q => q.addedOn ?? BASELINE_AS_OF).sort();
    return days[CATEGORY_LAUNCH_MIN - 1];
}
/** True when players can see and play the topic: every original topic always, a gated one from its launch day. */
export function isCategoryLive(key, today = todayAsOf()) {
    const meta = CATEGORIES[key];
    if (!meta)
        return false;
    if (!meta.gated)
        return (QUESTION_CATALOG[key]?.count ?? 0) > 0;
    const liveFrom = QUESTION_CATALOG[key]?.liveFrom;
    return Boolean(liveFrom) && liveFrom <= today;
}
/** A gated topic players cannot see yet: its questions must not be promoted (social posts) before the topic exists in the app. */
export const isCategoryHidden = (key, today = todayAsOf()) => Boolean(CATEGORIES[key]?.gated) && !isCategoryLive(key, today);
export function getCategoryStats() {
    return Object.entries(QUESTION_CATALOG)
        .map(([category, e]) => ({ category, count: e.count }))
        .sort((a, b) => a.category.localeCompare(b.category));
}
export function getCategoriesForLobby(today = todayAsOf()) {
    return getAllCategories().map(c => ({
        key: c.key,
        title: c.title,
        icon: c.icon,
        color: c.color,
        count: QUESTION_CATALOG[c.key]?.count ?? 0,
    })).filter(c => c.count > 0 && isCategoryLive(c.key, today));
}
export function getThemesForCategory(category) {
    return Object.entries(QUESTION_CATALOG[category]?.themes ?? {})
        .map(([theme, count]) => ({ theme, count }))
        .sort((a, b) => a.theme.localeCompare(b.theme));
}
